/**
 * Event Landing Page (Public)
 * USLS-branded, standardized registration page for events.
 * Single inline page: event context + registration form (one optional guest) → Success.
 * (No member/non-member quantity selectors; no separate registration step — paid checkout is not
 * expected.) Range Night uses a minimal field set; other event types keep the fuller set.
 * Route: /register/:id
 */

import React, { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { supabase, anonSupabase } from "../services/supabase";
import { US_STATES } from "../types/event.types";
import {
  parsePrefillParams,
  landingPageEnforceableCapacity,
} from "../utils/eventLandingPage";
import {
  getRegistrationDefault,
  fillRegistrationTemplate,
  parseRegistrationOffer,
  type RegistrationVariant,
} from "../utils/eventTypeRegistrationDefaults";
import USLSHeader from "../components/landing/USLSHeader";
import USLSFooter from "../components/landing/USLSFooter";
import AddToCalendar from "../components/landing/AddToCalendar";
import { SmsDisclosureText } from "../components/common/SmsDisclosureText";
import {
  SMS_CONSENT_SURFACES,
  SMS_DISCLOSURE_VERSIONS,
} from "../constants/smsConsentDisclosures";

const anonClient = anonSupabase;

// "How Did You Hear" default options (matching Salesforce)
const DEFAULT_HEAR_OPTIONS = [
  "Radio Ad",
  "Newspaper",
  "Facebook Ad / Facebook Post",
  "Newsletter / Email",
  "Gun Show / Gun Store",
  "Flyer",
  "Other",
];

type LandingPageView = "overview" | "registration" | "success";

interface LandingPageData {
  id: string;
  slug: string;
  landing_page_title: string | null;
  event_details_html: string | null;
  member_price: number | null;
  non_member_price: number | null;
  hear_about_options: string[];
  is_published: boolean;
  contact_department: string | null;
  contact_phone: string | null;
  contact_email: string | null;
  events: {
    id: string;
    title: string;
    description: string | null;
    start_time: string;
    end_time: string | null;
    timezone: string | null;
    location_name: string | null;
    location_address: Record<string, string> | null;
    status: string;
    event_status?: string | null;
    capacity: number | null;
    max_attendees: number | null;
    registration_open: boolean | null;
    registration_deadline: string | null;
  };
}

interface MemberCheckResponse {
  isMember: boolean;
  firstName?: string;
}

function formatEventDate(
  startTime: string,
  endTime?: string | null,
  timezone?: string | null,
): string {
  const tz = timezone || "America/Chicago";
  const opts: Intl.DateTimeFormatOptions = {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  };
  const start = new Date(startTime).toLocaleString("en-US", opts);
  if (endTime) {
    const endOpts: Intl.DateTimeFormatOptions = {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: tz,
    };
    const end = new Date(endTime).toLocaleString("en-US", endOpts);
    return `${start} - ${end}`;
  }
  return start;
}

// Time-only range (e.g. "6:00 PM – 10:00 PM") for inline copy — the full date already appears in
// the "Where & When" panel, so merge-field copy uses just the times to read naturally.
function formatEventTimeRange(
  startTime: string,
  endTime?: string | null,
  timezone?: string | null,
): string {
  const tz = timezone || "America/Chicago";
  const opts: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: tz,
  };
  const start = new Date(startTime).toLocaleTimeString("en-US", opts);
  if (endTime) {
    const end = new Date(endTime).toLocaleTimeString("en-US", opts);
    return `${start} – ${end}`;
  }
  return start;
}

function formatAddress(
  addr: Record<string, string> | null,
  locationName?: string | null,
): string {
  if (!addr) {
    return locationName || "";
  }
  const parts = [
    addr.street || addr.address_line1,
    addr.city,
    addr.state,
    addr.postal_code || addr.zip,
  ].filter(Boolean);
  return parts.join(", ");
}

function getGoogleMapsUrl(
  addr: Record<string, string> | null,
  locationName?: string | null,
): string {
  const q = formatAddress(addr, locationName);
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
}

function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
    FORBID_ATTR: [
      "onclick",
      "onload",
      "onerror",
      "onmouseover",
      "onfocus",
      "onblur",
    ],
  });
}

export default function EventLandingPage() {
  const { id: pageId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "true";
  // Phase 1.1 Ship 2: rep attribution comes from the `cta` query
  // parameter on the CTA URL. The backend validates ownership and
  // silently nulls a bad value, so we forward whatever's here.
  const ctaIdParam = searchParams.get("cta") || undefined;

  const [landingPage, setLandingPage] = useState<LandingPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // View state
  const [currentView, setCurrentView] = useState<LandingPageView>("overview");

  // Registration form
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    memberNumber: "",
    company: "",
    addressLine1: "",
    city: "",
    country: "US",
    state: "",
    postalCode: "",
    phone: "",
    howDidYouHear: "",
    messagingConsent: false,
    discountCode: "",
  });

  // Prefill from invite URL params (email / member number / first / last name) — once,
  // filling only blanks so a returning user's edits aren't clobbered.
  useEffect(() => {
    const pf = parsePrefillParams(searchParams);
    setFormData((prev) => ({
      ...prev,
      firstName: prev.firstName || pf.firstName || "",
      lastName: prev.lastName || pf.lastName || "",
      email: prev.email || pf.email || "",
      memberNumber: prev.memberNumber || pf.memberNumber || "",
      phone: prev.phone || pf.phone || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [memberCheck, setMemberCheck] = useState<MemberCheckResponse | null>(
    null,
  );
  const [checkingMember, setCheckingMember] = useState(false);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [submitting, setSubmitting] = useState(false);
  const [, setAvailablePasses] = useState<number | null>(null);
  const [isSoldOut, setIsSoldOut] = useState(false);
  /** Post-deploy audit HIGH 1: the honest ticket outcome, so the success screen cannot promise a
   *  ticket that was never minted. `issued: null` means we could not confirm — not "issued". */
  const [ticketOutcome, setTicketOutcome] = useState<{
    expected: boolean;
    issued: boolean | null;
    warning: string | null;
  } | null>(null);

  // Resolved partner name for locked registration templates ({{orgName}}): venue else the org
  // that owns the location (fetched via SECURITY DEFINER RPC — anon has no RLS on organizations).
  const [partnerName, setPartnerName] = useState<string | null>(null);
  // Registration-copy variant for locked templates (migration 392): 'insurance' for a Range Night whose venue
  // state is listed. Resolved server-side because the state list is not readable signed out.
  const [regVariant, setRegVariant] = useState<RegistrationVariant>("standard");
  const [guestData, setGuestData] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [guestResult, setGuestResult] = useState<{
    invited: boolean;
    warning: string | null;
  } | null>(null);

  // Load landing page data
  useEffect(() => {
    async function loadPage() {
      if (!pageId) {
        setError("No page specified");
        setLoading(false);
        return;
      }
      try {
        const client = isPreview ? supabase : anonClient;
        const { data, error: fetchError } = await client
          .from("event_landing_pages")
          .select(
            `*, events!inner(
            id, title, description, type, start_time, end_time, timezone,
            location_name, location_address, status,
            capacity, max_attendees, attendance_hard_cap,
            registration_open, registration_deadline
          )`,
          )
          .eq("id", pageId)
          .maybeSingle();

        if (fetchError) {
          throw fetchError;
        }
        if (!data) {
          setError("Page not found");
          setLoading(false);
          return;
        }
        if (!data.is_published && !isPreview) {
          setError("This page is not yet published");
          setLoading(false);
          return;
        }

        const event = data.events;
        const eventStatus = event.event_status || event.status || "";
        if (eventStatus === "cancelled" || eventStatus === "completed") {
          setError("This event is no longer accepting registrations");
          setLoading(false);
          return;
        }
        if (event.registration_open === false) {
          setError("Registration is currently closed for this event");
          setLoading(false);
          return;
        }
        if (
          event.registration_deadline &&
          new Date() > new Date(event.registration_deadline)
        ) {
          setError("The registration deadline has passed");
          setLoading(false);
          return;
        }

        // Check capacity by PEOPLE, not rows — a single registration can bring a party
        // of several. Sum party_member_count + party_nonmember_count (min 1/row, for
        // legacy rows with 0/0), matching the server-side enforcement in
        // event-landing-register / event-walkin-register.
        //
        // Which limit this page may enforce is decided by landingPageEnforceableCapacity,
        // which mirrors the server and is unit-tested against it. Do not inline the
        // expression here again — the previous inline version omitted the
        // attendance_hard_cap check and made this page stricter than its own endpoint.
        const maxCapacity = landingPageEnforceableCapacity(event);
        if (maxCapacity) {
          // Aggregate endpoint, not a row read. Anonymous callers no longer have SELECT on
          // event_attendees at all: the policy that used to allow this read restricted ROWS, not
          // COLUMNS, so it also exposed attendee emails, phones and bearer check-in codes
          // (audit H1 on 107e36b8 -- 260 readable codes on PROD). The greatest(...,1) floor and
          // the registered/attended predicate now live in public_event_capacity_used, so this
          // page and the server agree by construction.
          const { data: cap } = await anonClient.rpc(
            "public_event_capacity_used",
            { p_event_id: event.id },
          );
          const usedPeople =
            (cap as { used_people?: number } | null)?.used_people ?? 0;
          const remaining = maxCapacity - usedPeople;
          setAvailablePasses(remaining);
          if (remaining <= 0) {
            setIsSoldOut(true);
            setAvailablePasses(0);
          }
        }

        // EIB REGISTRATION CAP (event_ticketing_provisioning.open_issuance_cap — the wizard's
        // "Maximum tickets to issue"). Separate from max_attendees above and enforced at a different
        // chokepoint. Until now this page knew nothing about it, so a capped-out event rendered a
        // working form: the visitor registered successfully and the ticket mint then failed with
        // open_cap_reached, leaving them on the roster with no ticket and no explanation.
        //
        // Read through an RPC, not the table: event_ticketing_provisioning has RLS enabled whose only
        // policy is SELECT to `authenticated`, so anon is default-denied.
        //
        // Advisory only — event-landing-register re-checks server-side and the atomic reserve in
        // create_or_get_event_ticket_token remains the authority. A read failure leaves the form open
        // rather than falsely closing a live event; the server still refuses.
        //
        // NOTE: walk-ins are never refused by this cap, but their tickets DO count toward it, so a
        // busy door can close online registration early. That is intended.
        try {
          const { data: avail } = await client.rpc(
            "get_event_registration_availability",
            { p_event_id: event.id },
          );
          const row = Array.isArray(avail) ? avail[0] : avail;
          if (row?.registration_full) {
            setIsSoldOut(true);
            setAvailablePasses(0);
          }
        } catch {
          // Non-fatal: leave the form open and let the server decide.
        }

        setLandingPage(data as LandingPageData);

        // Locked registration templates (e.g. Range Night) fill {{orgName}} with the resolved
        // partner name: the venue, else the org that owns the location. Anon can't read
        // organizations directly, so resolve server-side. Best-effort (falls back to location_name).
        const evType = (data as unknown as { events?: { type?: string } })
          ?.events?.type;
        if (getRegistrationDefault(evType)?.locked) {
          try {
            const { data: pn } = await client.rpc(
              "get_landing_page_partner_name",
              { p_landing_page_id: pageId },
            );
            if (typeof pn === "string" && pn.trim()) {
              setPartnerName(pn.trim());
            }
          } catch (pnErr) {
            console.warn("Could not resolve partner name:", pnErr);
          }
          // Insurance-state copy (392). If the variant cannot be resolved, fail toward the insurance copy
          // (it only omits the Bonus Offer paragraph; showing that paragraph where it must not appear is worse).
          try {
            // Mig 1480: the offer (the event's own amount, the state's copy) or the variant.
            const { data: offer, error: vErr } = await client.rpc(
              "eib_registration_offer",
              { p_landing_page_id: pageId },
            );
            setRegVariant(vErr ? "insurance" : parseRegistrationOffer(offer));
          } catch (vErr) {
            console.warn("Could not resolve registration variant:", vErr);
            setRegVariant("insurance");
          }
        }
      } catch (err) {
        console.error("Error loading landing page:", err);
        setError("Failed to load page");
      } finally {
        setLoading(false);
      }
    }
    loadPage();
  }, [pageId, isPreview]);

  // Member check on email blur
  const handleEmailBlur = useCallback(async () => {
    const email = formData.email;
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return;
    }
    setCheckingMember(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/event-landing-register?action=check-member&email=${encodeURIComponent(email)}`,
      );
      if (response.ok) {
        const data = await response.json();
        setMemberCheck(data);
        if (data.firstName && !formData.firstName) {
          setFormData((prev) => ({ ...prev, firstName: data.firstName }));
        }
      }
    } catch (err) {
      console.error("Member check failed:", err);
    } finally {
      setCheckingMember(false);
    }
  }, [formData.email, formData.firstName]);

  const handleFieldChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  // Validate registration form
  const validate = (): boolean => {
    const errors: Record<string, string> = {};
    if (!formData.firstName.trim()) {
      errors.firstName = "First Name is required";
    }
    if (!formData.lastName.trim()) {
      errors.lastName = "Last Name is required";
    }
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Please enter a valid email";
    }
    if (!formData.phone.trim()) {
      errors.phone = "Phone is required";
    }
    // ZIP is required (ALL forms, including Range Night) so a member can be authoritatively
    // disambiguated from a household member sharing the same email — the backend rejects an
    // unconfirmable member claim, so a form without ZIP would 422 an active member.
    if (!formData.postalCode.trim()) {
      errors.postalCode = "ZIP / Postal Code is required";
    }
    // Range Night uses a minimal inline form — "How did you hear" is not collected there.
    if (!isRangeNight && !formData.howDidYouHear) {
      errors.howDidYouHear = "Please select how you heard about us";
    }
    // Messaging consent is required to register (business decision). Applies to both the
    // standard and Range Night forms, which share this validator.
    //
    // Round-2 audit MED: confirmed active members are EXEMPT, matching the walk-in and
    // guest-confirm paths (and matching the server, which no longer records consent for them).
    // Requiring a box the server then ignores asked members to agree to nothing.
    if (!memberCheck?.isMember && !formData.messagingConsent) {
      errors.messagingConsent =
        "Please check the box to agree to receive messaging in order to register.";
    }
    // Invite-a-guest: if the registrant started filling the guest block, require all three fields.
    if (hasGuestInput) {
      if (!guestData.firstName.trim()) {
        errors.guestFirstName = "Guest first name is required";
      }
      if (!guestData.lastName.trim()) {
        errors.guestLastName = "Guest last name is required";
      }
      if (!guestData.email.trim()) {
        errors.guestEmail = "Guest email is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestData.email.trim())) {
        errors.guestEmail = "Enter a valid guest email";
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit registration
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !pageId || !landingPage) {
      return;
    }

    // Preview is validation-only: do NOT call the edge function or write any data.
    // (A real submit needs is_published=true anyway; this keeps preview honest and
    // avoids the confusing "not found or not published" error.)
    if (isPreview) {
      setValidationErrors({
        _form:
          "Preview mode: your details are valid, but registrations aren't live until this page is published. Nothing was submitted.",
      });
      window.scrollTo(0, 0);
      return;
    }

    // TODO: Paid ticket checkout — when ticket price > 0, integrate a payment
    // flow (e.g. Stripe Checkout) before calling the registration endpoint.
    // Pending business team decision on payment processor and flow.

    // TODO: Member status check — on email blur, verify membership status
    // and auto-apply member pricing if the registrant is an existing member.

    setSubmitting(true);
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/event-landing-register`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            landingPageId: pageId,
            firstName: formData.firstName.trim(),
            lastName: formData.lastName.trim(),
            email: formData.email.trim().toLowerCase(),
            memberNumber: formData.memberNumber.trim() || undefined,
            phone: formData.phone || undefined,
            company: formData.company || undefined,
            addressLine1: formData.addressLine1 || undefined,
            city: formData.city || undefined,
            state: formData.state || undefined,
            postalCode: formData.postalCode || undefined,
            country: formData.country || undefined,
            howDidYouHear: formData.howDidYouHear || undefined,
            messagingConsent: formData.messagingConsent || undefined,
            // Names the surface whose disclosure was displayed; the server resolves the exact
            // wording from that and freezes it into the consent evidence row.
            consentSurface: formData.messagingConsent
              ? SMS_CONSENT_SURFACES.eventLanding
              : undefined,
            discountCode: formData.discountCode || undefined,
            // Single registrant (member/non-member quantity selectors removed everywhere); an
            // invited guest is counted separately when THEY confirm their own ticket.
            memberCount: 1,
            nonMemberCount: 0,
            // Phase 1.1 Ship 2 — forward CTA query param so the edge
            // function can attribute the rep.
            ctaId: ctaIdParam,
            // EIB Gate 5 — forward the per-recipient invite token from the ?invite= link so an
            // invite_gated event can consume it and bind the ticket to the invited contact.
            inviteToken: searchParams.get("invite") || undefined,
            // Invite-a-guest: forward the one optional named guest. The edge function mints their
            // invite + emails them; their ticket is issued when THEY confirm.
            ...(hasGuestInput
              ? {
                  guestFirstName: guestData.firstName.trim(),
                  guestLastName: guestData.lastName.trim(),
                  guestEmail: guestData.email.trim().toLowerCase(),
                }
              : {}),
          }),
        },
      );
      const data = await response.json();
      if (!response.ok) {
        setValidationErrors({ _form: data.error || "Registration failed" });
        return;
      }
      setGuestResult(
        hasGuestInput
          ? { invited: !!data.guestInvited, warning: data.guestWarning ?? null }
          : null,
      );
      // The server now reports whether an admission ticket actually exists. Previously this
      // response carried rewardWarning and the page discarded it entirely, so a registrant with
      // no ticket saw an unconditional "You're registered!" — the defect the audit found.
      setTicketOutcome({
        expected: !!data.ticketExpected,
        issued:
          data.ticketIssued === undefined ? null : (data.ticketIssued ?? null),
        warning: data.rewardWarning ?? null,
      });
      setCurrentView("success");
      window.scrollTo(0, 0);
    } catch (err) {
      console.error("Registration error:", err);
      setValidationErrors({ _form: "An error occurred. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  // Computed values
  const event = landingPage?.events;
  // Range Night uses a streamlined single-page flow: no member/non-member quantity selectors, the
  // registrant's details are collected INLINE on the overview (no second view), and a single guest
  // (name + email) can be invited. Members-only assumption — no member/non-member declaration.
  const isRangeNight =
    (event as unknown as { type?: string | null })?.type === "range_night";
  const pageTitle = landingPage?.landing_page_title || event?.title || "Event";
  const hearOptions = landingPage?.hear_about_options?.length
    ? landingPage.hear_about_options
    : DEFAULT_HEAR_OPTIONS;
  // Member-focused MVP: default to a members-only audience unless explicitly 'public'.
  const audienceType =
    (landingPage?.field_config as { audience_type?: string } | undefined)
      ?.audience_type === "public"
      ? "public"
      : "members";
  const membersOnly = audienceType === "members";

  // The registrant filled at least one guest field → they intend to invite a guest.
  const hasGuestInput =
    guestData.firstName.trim() !== "" ||
    guestData.lastName.trim() !== "" ||
    guestData.email.trim() !== "";

  // Loading
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#C8102E]" />
      </div>
    );
  }

  // Error
  if (error) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <USLSHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-md mx-auto px-4">
            <h1 className="text-2xl font-bold text-[#1B2A4A] mb-2">
              {error === "Page not found" ? "Page Not Found" : "Unavailable"}
            </h1>
            <p className="text-gray-600">{error}</p>
          </div>
        </div>
        <USLSFooter />
      </div>
    );
  }

  if (!landingPage || !event) {
    return null;
  }

  // ========================= SUCCESS VIEW =========================
  if (currentView === "success") {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <USLSHeader />
        <div className="flex-1 flex items-center justify-center py-16">
          <div className="text-center max-w-lg mx-auto px-4">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg
                className="w-10 h-10 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-3xl font-bold text-[#1B2A4A] mb-4">
              You're registered!
            </h1>
            {!(ticketOutcome?.expected && ticketOutcome.issued !== true) && (
              <p className="text-lg text-gray-600 mb-2">
                We're sending you an email with the event details as we speak!
              </p>
            )}
            <p className="text-lg text-gray-600 mb-6">We'll see you soon!</p>
            {/* Ticket outcome. Only speaks when a ticket was EXPECTED — an ordinary event that
                issues no ticket must not be told one is missing. `issued === false` is a real
                registered-without-a-ticket outcome; `null` means unconfirmed, which we say
                plainly rather than dressing up as success. */}
            {ticketOutcome?.expected && ticketOutcome.issued !== true && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-amber-900 text-base text-left">
                <p className="font-semibold mb-1">
                  {ticketOutcome.issued === false
                    ? "Your place is saved, but your event ticket wasn't issued."
                    : "Your place is saved, but we couldn't confirm your event ticket."}
                </p>
                <p>
                  You are on the list for this event. Please contact the Events
                  Department at{" "}
                  <a
                    className="underline"
                    href="mailto:Seminars@USLawShield.com"
                  >
                    Seminars@USLawShield.com
                  </a>{" "}
                  or 855-379-8994 and they will issue your ticket — you do not
                  need to register again.
                </p>
              </div>
            )}
            {ticketOutcome?.warning && ticketOutcome.issued !== false && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-amber-800 text-sm">
                {ticketOutcome.warning}
              </div>
            )}
            {guestResult?.invited && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 text-green-800 text-base">
                Your guest has been invited — we&rsquo;ve emailed them a link to
                confirm and get their ticket.
              </div>
            )}
            {guestResult?.warning && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 text-amber-800 text-sm">
                {guestResult.warning}
              </div>
            )}
            <AddToCalendar
              title={pageTitle}
              startTime={event.start_time}
              endTime={event.end_time}
              timezone={event.timezone}
              locationName={event.location_name}
              locationAddress={formatAddress(event.location_address)}
              className="inline-flex items-center gap-2 text-[#C8102E] font-medium text-lg hover:underline"
            />
          </div>
        </div>
        <USLSFooter />
      </div>
    );
  }

  // Registration form — rendered INLINE in the overview's Reserve card for all non-Range-Night
  // event types (one single page; there is no separate registration view). Range Night uses its
  // own minimal inline form.
  const registrationForm = (
    <form onSubmit={handleSubmit}>
      {validationErrors._form && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          {validationErrors._form}
        </div>
      )}

      {/* First + Last Name */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <span className="text-red-500">*</span> First Name
          </label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => handleFieldChange("firstName", e.target.value)}
            placeholder="First Name"
            required
            className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.firstName ? "border-red-300" : "border-gray-300"}`}
          />
          {validationErrors.firstName && (
            <p className="text-xs text-red-500 mt-1">
              {validationErrors.firstName}
            </p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <span className="text-red-500">*</span> Last Name
          </label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => handleFieldChange("lastName", e.target.value)}
            placeholder="Last Name"
            required
            className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.lastName ? "border-red-300" : "border-gray-300"}`}
          />
          {validationErrors.lastName && (
            <p className="text-xs text-red-500 mt-1">
              {validationErrors.lastName}
            </p>
          )}
        </div>
      </div>

      {/* Email */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <span className="text-red-500">*</span> Email
        </label>
        <input
          type="email"
          value={formData.email}
          onChange={(e) => handleFieldChange("email", e.target.value)}
          onBlur={handleEmailBlur}
          placeholder="Email"
          required
          className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.email ? "border-red-300" : "border-gray-300"}`}
        />
        {checkingMember && (
          <p className="text-xs text-gray-400 mt-1">Checking membership...</p>
        )}
        {memberCheck && !checkingMember && (
          <p
            className={`text-xs mt-1 ${memberCheck.isMember ? "text-green-600" : "text-gray-500"}`}
          >
            {memberCheck.isMember
              ? "Active membership found — we'll confirm your details on submit"
              : "Not a current member"}
          </p>
        )}
        {validationErrors.email && (
          <p className="text-xs text-red-500 mt-1">{validationErrors.email}</p>
        )}
      </div>

      {/* Member Number (optional; prefilled from invite URL when present) */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Member Number (optional)
        </label>
        <input
          type="text"
          value={formData.memberNumber}
          onChange={(e) => handleFieldChange("memberNumber", e.target.value)}
          placeholder="Member Number"
          autoCapitalize="off"
          autoCorrect="off"
          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E]"
        />
      </div>

      {/* Company */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Company (optional)
        </label>
        <input
          type="text"
          value={formData.company}
          onChange={(e) => handleFieldChange("company", e.target.value)}
          placeholder="Company (optional)"
          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E]"
        />
      </div>

      {/* Street + City */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Street Address (optional)
          </label>
          <input
            type="text"
            value={formData.addressLine1}
            onChange={(e) => handleFieldChange("addressLine1", e.target.value)}
            placeholder="Street Address (optional)"
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City (optional)
          </label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => handleFieldChange("city", e.target.value)}
            placeholder="City (optional)"
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E]"
          />
        </div>
      </div>

      {/* Country + State */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Country
          </label>
          <select
            value={formData.country}
            onChange={(e) => handleFieldChange("country", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E]"
          >
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="MX">Mexico</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            State/Province
          </label>
          <select
            value={formData.state}
            onChange={(e) => handleFieldChange("state", e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E]"
          >
            <option value="">-- State/Province --</option>
            {US_STATES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Postal Code — required for member disambiguation (see validate()) */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          ZIP / Postal Code *
        </label>
        <input
          type="text"
          value={formData.postalCode}
          onChange={(e) => handleFieldChange("postalCode", e.target.value)}
          placeholder="ZIP / Postal Code"
          required
          className="w-full max-w-xs px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E]"
        />
        {validationErrors.postalCode && (
          <p className="text-red-600 text-xs mt-1">
            {validationErrors.postalCode}
          </p>
        )}
      </div>

      {/* Phone */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <span className="text-red-500">*</span> Phone
        </label>
        <input
          type="tel"
          value={formData.phone}
          onChange={(e) => handleFieldChange("phone", e.target.value)}
          placeholder="Phone"
          required
          className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.phone ? "border-red-300" : "border-gray-300"}`}
        />
        {validationErrors.phone && (
          <p className="text-xs text-red-500 mt-1">{validationErrors.phone}</p>
        )}
      </div>

      {/* How Did You Hear */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <span className="text-red-500">*</span> How Did You Hear About Us
        </label>
        <select
          value={formData.howDidYouHear}
          onChange={(e) => handleFieldChange("howDidYouHear", e.target.value)}
          required
          className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.howDidYouHear ? "border-red-300" : "border-gray-300"}`}
        >
          <option value="">-- choose one --</option>
          {hearOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {validationErrors.howDidYouHear && (
          <p className="text-xs text-red-500 mt-1">
            {validationErrors.howDidYouHear}
          </p>
        )}
      </div>

      {/* Invite a guest — one optional named guest (all event types). */}
      <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
        <h3 className="text-sm font-semibold text-[#1B2A4A] mb-1">
          Bring a guest (optional)
        </h3>
        <p className="text-xs text-gray-500 mb-3">
          Add one guest and we&rsquo;ll email them an invite to confirm their
          own ticket.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <input
              type="text"
              value={guestData.firstName}
              onChange={(e) =>
                setGuestData((g) => ({
                  ...g,
                  firstName: e.target.value,
                }))
              }
              placeholder="Guest first name"
              className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.guestFirstName ? "border-red-300" : "border-gray-300"}`}
            />
            {validationErrors.guestFirstName && (
              <p className="text-xs text-red-500 mt-1">
                {validationErrors.guestFirstName}
              </p>
            )}
          </div>
          <div>
            <input
              type="text"
              value={guestData.lastName}
              onChange={(e) =>
                setGuestData((g) => ({
                  ...g,
                  lastName: e.target.value,
                }))
              }
              placeholder="Guest last name"
              className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.guestLastName ? "border-red-300" : "border-gray-300"}`}
            />
            {validationErrors.guestLastName && (
              <p className="text-xs text-red-500 mt-1">
                {validationErrors.guestLastName}
              </p>
            )}
          </div>
          <div>
            <input
              type="email"
              value={guestData.email}
              onChange={(e) =>
                setGuestData((g) => ({ ...g, email: e.target.value }))
              }
              placeholder="Guest email"
              className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.guestEmail ? "border-red-300" : "border-gray-300"}`}
            />
            {validationErrors.guestEmail && (
              <p className="text-xs text-red-500 mt-1">
                {validationErrors.guestEmail}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Messaging Consent — hidden for a confirmed active member, who is exempt from re-opting in
          (the server does not record consent for them either). */}
      <div className={`mb-4 ${memberCheck?.isMember ? "hidden" : ""}`}>
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={formData.messagingConsent}
            onChange={(e) =>
              handleFieldChange("messagingConsent", e.target.checked)
            }
            className="mt-1 rounded border-gray-300 text-[#C8102E] focus:ring-[#C8102E]"
          />
          {/* Rendered from the canonical registry so the words shown here stay byte-identical to
              the disclosure frozen into the consent evidence row. */}
          <SmsDisclosureText
            version={SMS_DISCLOSURE_VERSIONS.eventLanding}
            className="text-xs text-gray-600 leading-relaxed"
          />
        </label>
        {validationErrors.messagingConsent && (
          <p className="text-xs text-red-500 mt-1">
            {validationErrors.messagingConsent}
          </p>
        )}
      </div>

      {/* Discount Code */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Discount Code
        </label>
        <input
          type="text"
          value={formData.discountCode}
          onChange={(e) => handleFieldChange("discountCode", e.target.value)}
          placeholder="Discount Code"
          className="w-full max-w-md px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E]"
        />
      </div>

      {/* Submit */}
      <div className="text-center">
        <button
          type="submit"
          disabled={isSoldOut || submitting}
          className="px-8 py-3 bg-[#1B2A4A] text-white font-medium rounded hover:bg-[#2a3d66] focus:outline-none focus:ring-2 focus:ring-[#C8102E] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {submitting
            ? "Submitting..."
            : isSoldOut
              ? "Sold Out"
              : "Submit Registration"}
        </button>
      </div>
    </form>
  );

  // ========================= OVERVIEW VIEW =========================
  // Some event types (e.g. Range Night) ship a LOCKED bundled banner + Event Detail copy — the
  // public page renders those instead of any stored/authored copy, with only event time + partner
  // (org) name merged in from the event record.
  const regDefault = getRegistrationDefault(
    (event as unknown as { type?: string | null })?.type,
    regVariant,
  );
  // Partner name for {{orgName}}: the RPC-resolved name (venue else the org that owns the location);
  // fall back to the venue string already on the event if the RPC hasn't resolved.
  const orgName = partnerName || event?.location_name || null;
  const eventDetailsHtml = regDefault?.locked
    ? fillRegistrationTemplate(regDefault.eventDetailsTemplate, {
        eventTime: event?.start_time
          ? formatEventTimeRange(
              event.start_time,
              event.end_time,
              event.timezone,
            )
          : null,
        orgName,
      })
    : landingPage.event_details_html || event.description || "";

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <USLSHeader />
      {isPreview && (
        <div className="bg-yellow-400 text-yellow-900 text-center py-2 text-sm font-medium">
          Preview Mode — This page is not published yet
        </div>
      )}

      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 py-8">
          {regDefault?.bannerImageUrl && (
            <img
              src={regDefault.bannerImageUrl}
              alt={regDefault.bannerAlt}
              className="w-full h-auto rounded-lg mb-8"
            />
          )}
          {/* Event Title */}
          <h1 className="text-4xl font-bold text-[#1B2A4A] italic mb-8">
            {pageTitle}
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column (2/3) */}
            <div className="lg:col-span-2 space-y-8">
              {/* Reserve Your Seat */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h2 className="text-2xl font-bold text-[#1B2A4A] uppercase mb-4">
                  {membersOnly
                    ? "Reserve Your Seat (USLS Members)"
                    : "Reserve Your Seat"}
                </h2>
                {membersOnly && (
                  <p className="text-sm text-gray-600 mb-4 -mt-2">
                    This is a U.S. LawShield member event. Reserve your member
                    seat below.
                  </p>
                )}

                {isSoldOut && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4 flex items-center gap-3">
                    <svg
                      className="w-6 h-6 text-red-500 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                    <div>
                      <p className="font-bold text-red-800">
                        This event is sold out.
                      </p>
                      <p className="text-sm text-red-600">
                        Registration is no longer available as all passes have
                        been claimed.
                      </p>
                    </div>
                  </div>
                )}
                {isRangeNight ? (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <p className="text-sm text-gray-600 -mt-2">
                      Reserve your seat below. You can also invite one guest —
                      we&rsquo;ll email them their own invitation.
                    </p>
                    {validationErrors._form && (
                      <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                        {validationErrors._form}
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <span className="text-red-500">*</span> First Name
                        </label>
                        <input
                          type="text"
                          value={formData.firstName}
                          onChange={(e) =>
                            handleFieldChange("firstName", e.target.value)
                          }
                          placeholder="First Name"
                          className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.firstName ? "border-red-300" : "border-gray-300"}`}
                        />
                        {validationErrors.firstName && (
                          <p className="text-xs text-red-500 mt-1">
                            {validationErrors.firstName}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <span className="text-red-500">*</span> Last Name
                        </label>
                        <input
                          type="text"
                          value={formData.lastName}
                          onChange={(e) =>
                            handleFieldChange("lastName", e.target.value)
                          }
                          placeholder="Last Name"
                          className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.lastName ? "border-red-300" : "border-gray-300"}`}
                        />
                        {validationErrors.lastName && (
                          <p className="text-xs text-red-500 mt-1">
                            {validationErrors.lastName}
                          </p>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <span className="text-red-500">*</span> Email
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          handleFieldChange("email", e.target.value)
                        }
                        onBlur={handleEmailBlur}
                        placeholder="Email"
                        className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.email ? "border-red-300" : "border-gray-300"}`}
                      />
                      {checkingMember && (
                        <p className="text-xs text-gray-400 mt-1">
                          Checking membership...
                        </p>
                      )}
                      {memberCheck && !checkingMember && (
                        <p
                          className={`text-xs mt-1 ${memberCheck.isMember ? "text-green-600" : "text-gray-500"}`}
                        >
                          {memberCheck.isMember
                            ? "Active membership found — we'll confirm your details on submit"
                            : "Not a current member"}
                        </p>
                      )}
                      {validationErrors.email && (
                        <p className="text-xs text-red-500 mt-1">
                          {validationErrors.email}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          <span className="text-red-500">*</span> Phone
                        </label>
                        <input
                          type="tel"
                          value={formData.phone}
                          onChange={(e) =>
                            handleFieldChange("phone", e.target.value)
                          }
                          placeholder="Phone"
                          className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.phone ? "border-red-300" : "border-gray-300"}`}
                        />
                        {validationErrors.phone && (
                          <p className="text-xs text-red-500 mt-1">
                            {validationErrors.phone}
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          State/Province
                        </label>
                        <select
                          value={formData.state}
                          onChange={(e) =>
                            handleFieldChange("state", e.target.value)
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E]"
                        >
                          <option value="">-- State/Province --</option>
                          {US_STATES.map((s) => (
                            <option key={s.code} value={s.code}>
                              {s.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <span className="text-red-500">*</span> ZIP / Postal
                        Code
                      </label>
                      <input
                        type="text"
                        value={formData.postalCode}
                        onChange={(e) =>
                          handleFieldChange("postalCode", e.target.value)
                        }
                        placeholder="ZIP / Postal Code"
                        required
                        className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.postalCode ? "border-red-300" : "border-gray-300"}`}
                      />
                      {validationErrors.postalCode && (
                        <p className="text-xs text-red-500 mt-1">
                          {validationErrors.postalCode}
                        </p>
                      )}
                    </div>
                    <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <h3 className="text-sm font-semibold text-[#1B2A4A] mb-1">
                        Bring a guest (optional)
                      </h3>
                      <p className="text-xs text-gray-500 mb-3">
                        Add one guest and we&rsquo;ll email them an invite to
                        confirm their own ticket.
                      </p>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <div>
                          <input
                            type="text"
                            value={guestData.firstName}
                            onChange={(e) =>
                              setGuestData((g) => ({
                                ...g,
                                firstName: e.target.value,
                              }))
                            }
                            placeholder="Guest first name"
                            className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.guestFirstName ? "border-red-300" : "border-gray-300"}`}
                          />
                          {validationErrors.guestFirstName && (
                            <p className="text-xs text-red-500 mt-1">
                              {validationErrors.guestFirstName}
                            </p>
                          )}
                        </div>
                        <div>
                          <input
                            type="text"
                            value={guestData.lastName}
                            onChange={(e) =>
                              setGuestData((g) => ({
                                ...g,
                                lastName: e.target.value,
                              }))
                            }
                            placeholder="Guest last name"
                            className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.guestLastName ? "border-red-300" : "border-gray-300"}`}
                          />
                          {validationErrors.guestLastName && (
                            <p className="text-xs text-red-500 mt-1">
                              {validationErrors.guestLastName}
                            </p>
                          )}
                        </div>
                        <div>
                          <input
                            type="email"
                            value={guestData.email}
                            onChange={(e) =>
                              setGuestData((g) => ({
                                ...g,
                                email: e.target.value,
                              }))
                            }
                            placeholder="Guest email"
                            className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#C8102E] ${validationErrors.guestEmail ? "border-red-300" : "border-gray-300"}`}
                          />
                          {validationErrors.guestEmail && (
                            <p className="text-xs text-red-500 mt-1">
                              {validationErrors.guestEmail}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                    <label
                      className={`flex items-start gap-3 cursor-pointer ${
                        memberCheck?.isMember ? "hidden" : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.messagingConsent}
                        onChange={(e) =>
                          handleFieldChange(
                            "messagingConsent",
                            e.target.checked,
                          )
                        }
                        className="mt-1 rounded border-gray-300 text-[#C8102E] focus:ring-[#C8102E]"
                      />
                      {/* Same canonical registry as the standard form. Previously this Range Night
                          variant showed materially DIFFERENT copy (no Privacy Policy sentence) while
                          both posted the same consent flag — one of the two could not have matched
                          whatever the server stored. */}
                      <SmsDisclosureText
                        version={SMS_DISCLOSURE_VERSIONS.eventLanding}
                        className="text-xs text-gray-600 leading-relaxed"
                      />
                    </label>
                    {validationErrors.messagingConsent && (
                      <p className="text-xs text-red-500">
                        {validationErrors.messagingConsent}
                      </p>
                    )}
                    <div className="text-right">
                      <button
                        type="submit"
                        disabled={isSoldOut || submitting}
                        className="px-6 py-2.5 bg-[#1B2A4A] text-white font-medium rounded hover:bg-[#2a3d66] disabled:opacity-40 disabled:cursor-not-allowed transition-colors uppercase tracking-wide text-sm"
                      >
                        {submitting
                          ? "Submitting..."
                          : isSoldOut
                            ? "Sold Out"
                            : "Reserve My Seat"}
                      </button>
                    </div>
                  </form>
                ) : (
                  registrationForm
                )}
              </div>

              {/* Event Details */}
              {eventDetailsHtml && (
                <div className="border border-gray-200 rounded-lg p-6">
                  <h2 className="text-2xl font-bold text-[#1B2A4A] uppercase mb-4">
                    Event Details
                  </h2>
                  <div
                    className="prose prose-sm max-w-none text-gray-700"
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(eventDetailsHtml),
                    }}
                  />
                </div>
              )}
            </div>

            {/* Right Column (1/3) */}
            <div className="space-y-6">
              {/* Where & When */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h2 className="text-xl font-bold text-[#1B2A4A] uppercase mb-4">
                  Where &amp; When
                </h2>

                {/* Date/Time */}
                <div className="flex items-start gap-3 mb-4">
                  <svg
                    className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <p className="text-sm text-gray-700">
                      {formatEventDate(
                        event.start_time,
                        event.end_time,
                        event.timezone,
                      )}
                    </p>
                    <AddToCalendar
                      title={pageTitle}
                      startTime={event.start_time}
                      endTime={event.end_time}
                      timezone={event.timezone}
                      locationName={event.location_name}
                      locationAddress={formatAddress(event.location_address)}
                    />
                  </div>
                </div>

                {/* Location */}
                {(event.location_name || event.location_address) && (
                  <div className="flex items-start gap-3 mb-4">
                    <svg
                      className="w-5 h-5 text-gray-500 mt-0.5 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <div>
                      {event.location_name && (
                        <p className="text-sm font-medium text-gray-800">
                          {event.location_name}
                        </p>
                      )}
                      {event.location_address && (
                        <p className="text-sm text-gray-600">
                          {formatAddress(event.location_address)}
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Map embed */}
                {(event.location_name || event.location_address) &&
                  (() => {
                    const mapQuery = encodeURIComponent(
                      [
                        formatAddress(event.location_address),
                        event.location_name,
                      ]
                        .filter(Boolean)
                        .join(", "),
                    );
                    return (
                      <div className="mt-4">
                        <div className="rounded-lg overflow-hidden border border-gray-200">
                          <iframe
                            title="Event location map"
                            width="100%"
                            height="200"
                            style={{ border: 0 }}
                            loading="lazy"
                            allowFullScreen
                            referrerPolicy="no-referrer-when-downgrade"
                            src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${mapQuery}`}
                          />
                        </div>
                        <a
                          href={getGoogleMapsUrl(
                            event.location_address,
                            event.location_name,
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block mt-2 text-sm text-[#C8102E] hover:underline"
                        >
                          View on Google Maps &rarr;
                        </a>
                      </div>
                    );
                  })()}
              </div>

              {/* Contact */}
              <div className="border border-gray-200 rounded-lg p-6">
                <h2 className="text-xl font-bold text-[#1B2A4A] uppercase mb-3">
                  Contact
                </h2>
                <p className="text-sm font-medium text-gray-800">
                  {landingPage.contact_department || "Events Department"}
                </p>
                <a
                  href={`tel:${(landingPage.contact_phone || "855-379-8994").replace(/[^0-9+]/g, "")}`}
                  className="text-sm text-[#C8102E] hover:underline block"
                >
                  {landingPage.contact_phone || "855-379-8994"}
                </a>
                <a
                  href={`mailto:${landingPage.contact_email || "Seminars@USLawShield.com"}`}
                  className="text-sm text-[#C8102E] hover:underline block"
                >
                  {landingPage.contact_email || "Seminars@USLawShield.com"}
                </a>
              </div>
            </div>
          </div>

          {/* Social Share */}
          <div className="mt-8 flex gap-4">
            <a
              href={`https://www.facebook.com/sharer.php?u=${encodeURIComponent(window.location.href)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-[#1877F2]"
              aria-label="Share on Facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
              </svg>
            </a>
            <a
              href={`https://www.twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(pageTitle)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-[#1DA1F2]"
              aria-label="Share on Twitter"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" />
              </svg>
            </a>
            <a
              href={`https://www.linkedin.com/shareArticle?url=${encodeURIComponent(window.location.href)}&mini=true&title=${encodeURIComponent(pageTitle)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-[#0A66C2]"
              aria-label="Share on LinkedIn"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-4 0v7h-4v-7a6 6 0 016-6zM2 9h4v12H2zM4 2a2 2 0 110 4 2 2 0 010-4z" />
              </svg>
            </a>
          </div>
        </div>
      </main>
      <USLSFooter />
    </div>
  );
}
